import { DATA_SERVICE_BASE_URL } from './ApiService';
import { fetchWithAuth } from './AuthService';

/**
 * Base URL for the Payments Service (proxied through main data service)
 * Adjust DATA_SERVICE_BASE_URL via runtime config or env as needed.
 */
// Allow direct override of Payments Service base (bypassing data-service proxy) via runtime config or env.
// This helps local dev when the proxy route is not yet configured and avoids 502 errors.
const rc = (typeof window !== 'undefined' ? (window.RUNTIME_CONFIG || window.__RUNTIME_CONFIG__ || {}) : {});
const DIRECT_PAYMENTS_BASE = rc.PAYMENTS_SERVICE_BASE_URL ||
  rc.REACT_APP_PAYMENTS_SERVICE_BASE_URL ||
  process.env.REACT_APP_PAYMENTS_SERVICE_BASE_URL ||
  process.env.PAYMENTS_SERVICE_BASE_URL || null;

export const PAYMENTS_SERVICE_BASE_URL = DIRECT_PAYMENTS_BASE || `${DATA_SERVICE_BASE_URL}/api/proxy/payments-service`;
// Backward-compatible alias
export const PAYMENTS_SERVICE_API_BASE_URL = PAYMENTS_SERVICE_BASE_URL;

/* Internal helpers */
async function request(path, { method = 'GET', body, headers, raw = false } = {}) {
  const url = `${PAYMENTS_SERVICE_BASE_URL}${path}`;
  const opts = {
    method,
    headers: {
      ...(body && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...headers
    },
    body: body && !(body instanceof FormData) ? JSON.stringify(body) : body
  };
  const resp = await fetchWithAuth(url, opts);
  if (raw) return resp;
  if (resp.status === 204) return null;
  let json;
  try { json = await resp.json(); } catch { json = null; }
  if (!resp.ok) {
  const msg = (json && (json.error || json.message)) || `HTTP ${resp.status}`;
  console.error('[PaymentsServiceApi] Request failed', { url, status: resp.status, msg, body: json });
    throw new Error(msg);
  }
  return json;
}

/**
 * PaymentsServiceApi
 * Mirrors endpoints defined in PaymentsService.postman_collection.json
 * NOTE: Do NOT expose webhook calls from client (excluded).
 */
export const PaymentsServiceApi = {
  // Health
  health: () => request('/health'),

  // Payments (generic records - if implemented server-side)
  listPayments: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.limit) qp.set('limit', params.limit);
    if (params.status) qp.set('status', params.status);
    if (params.providerStatus) qp.set('providerStatus', params.providerStatus);
    if (params.fromDate) qp.set('fromDate', params.fromDate);
    if (params.toDate) qp.set('toDate', params.toDate);
    const qs = qp.toString();
    return request(`/payments${qs ? `?${qs}` : ''}`);
  },
  listRecentPayments: () => request('/payments/recent'),
  /**
   * List available subscription plans
   * @returns {Promise<Array<{alias: string, label: string, amount: number, currency: string, interval: string, priceId: string}>>}
   */
  listPlans: async () => {
    const resp = await request('/payments/plans');
    // Support either bare array or wrapped { plans: [...] }
    if (Array.isArray(resp)) return resp;
    if (resp && Array.isArray(resp.plans)) return resp.plans;
    return [];
  },
  getPaymentsSummary: () => request('/payments/summary'),
  createPaymentRecord: (data) => request('/payments', { method: 'POST', body: data }),
  getPayment: (id) => request(`/payments/${id}`),
  getPaymentStatus: (id) => request(`/payments/${id}/status`),
  reconcilePayment: (id) => request(`/payments/${id}/reconcile`, { method: 'POST' }),
  getPaymentTimeline: (id) => request(`/payments/${id}/timeline`),
  patchPaymentMetadata: (id, metadata) => request(`/payments/${id}/metadata`, { method: 'PATCH', body: { metadata } }),
  putPaymentMetadata: (id, metadata) => request(`/payments/${id}/metadata`, { method: 'PUT', body: { metadata } }),
  deletePaymentMetadataKey: (id, key) => request(`/payments/${id}/metadata/${encodeURIComponent(key)}`, { method: 'DELETE' }),
  refundPaymentRecord: (id) => request(`/payments/${id}/refund`, { method: 'POST' }),
  cancelPaymentRecord: (id) => request(`/payments/${id}/cancel`, { method: 'POST' }),

  // Payment Intents (one-off payments / manual flows)
  createPaymentIntent: (data) => request('/payments/intents', { method: 'POST', body: data }),
  getPaymentIntent: (id) => request(`/payments/intents/${id}`),
  cancelPaymentIntent: (id) => request(`/payments/intents/${id}/cancel`, { method: 'POST' }),
  capturePaymentIntent: (id, amountToCapture) =>
    request(`/payments/intents/${id}/capture`, { method: 'POST', body: { amountToCapture } }),
  refundPaymentIntent: (id, amount) =>
    request(`/payments/intents/${id}/refund`, { method: 'POST', body: { amount } }),

  // Ephemeral Keys (typically for mobile SDK usage)
  createEphemeralKey: ({ customerId, apiVersion }) =>
    request('/payments/ephemeral-keys', { method: 'POST', body: { customerId, apiVersion } }),

  /**
   * Create Stripe Checkout Session for subscription purchase or modification
   * @param {object} params
   * @param {string} params.planAlias - Plan alias (e.g., 'basic', 'pro')
   * @param {string} params.successUrl - URL to redirect after successful checkout
   * @param {string} params.cancelUrl - URL to redirect if checkout is canceled
   * @param {string} [params.idempotencyKey] - Optional idempotency key for safe retries
   * @param {string} [params.priceId] - Deprecated: use planAlias instead
   * @returns {Promise<{sessionId?: string, url?: string}>}
   */
  createCheckoutSession: ({ planAlias, successUrl, cancelUrl, idempotencyKey, priceId }) => {
    // Backwards compatibility: allow priceId param but send planAlias if provided
    const body = planAlias ? { planAlias, successUrl, cancelUrl } : { priceId, successUrl, cancelUrl };
    const headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
    return request('/payments/checkout-session', { method: 'POST', body, headers });
  },

  // (Webhook intentionally NOT exposed)

  // Checkout (custom one-off intent creation route)
  createCheckoutPaymentIntent: (data) =>
    request('/checkout/create-payment-intent', { method: 'POST', body: data }),

  getSubscription: () => request('/subscriptions/subscription'),
  createSetupIntent: () => request('/subscriptions/create-setup-intent', { method: 'POST' }),
  attachPaymentMethod: (paymentMethodId) =>
    request('/subscriptions/attach-payment-method', {
      method: 'POST',
      body: { paymentMethodId }
    }),
  createSubscriptionDirect: (priceKey) =>
    request('/subscriptions/create-subscription', { method: 'POST', body: { priceKey } }),

  /**
   * Cancel the current subscription
   * @param {boolean} [atPeriodEnd=true] - If true, cancel at end of billing period; if false, cancel immediately
   * @returns {Promise<object>}
   */
  cancelSubscription: (atPeriodEnd = true) =>
    request('/subscriptions/cancel-subscription', {
      method: 'POST',
      body: { atPeriodEnd }
    }),

  /**
   * Resume a canceled subscription (removes cancel_at_period_end flag)
   * @returns {Promise<object>}
   */
  resumeSubscription: () =>
    request('/subscriptions/resume-subscription', { method: 'POST' }),

  listInvoices: (limit = 10) =>
    request(`/subscriptions/invoices?limit=${encodeURIComponent(limit)}`),

  /**
   * Create Stripe billing portal session for customer to manage payment methods, invoices, etc.
   * @param {string} returnUrl - URL to return to after exiting the portal
   * @returns {Promise<{url: string}>}
   */
  createPortalSession: (returnUrl) =>
    request('/subscriptions/create-portal-session', {
      method: 'POST',
      body: { returnUrl }
    }),

  /**
   * --- Payment Methods (customer) ---
   * These endpoints are commonly exposed by Stripe-connect style services. If backend supports different path names, adjust here.
   * listPaymentMethods: returns array of payment methods attached to current customer (card / bank, etc.)
   * detachPaymentMethod: removes a payment method from the customer
   * setDefaultPaymentMethod: sets a payment method as invoice/default for subscription billing
   */
  listPaymentMethods: () => request('/subscriptions/payment-methods'),
  detachPaymentMethod: (paymentMethodId) =>
    request(`/subscriptions/payment-methods/${encodeURIComponent(paymentMethodId)}/detach`, { method: 'POST' }),
  setDefaultPaymentMethod: (paymentMethodId) =>
    request('/subscriptions/set-default-payment-method', { method: 'POST', body: { paymentMethodId } }),

  /**
   * Modify subscription (change tiers) – switches an active subscription to a new plan.
   * Backend handles proration and Stripe subscription update. Returns tier info.
   * @param {string} planAliasOrPriceKey - The plan alias (e.g., 'basic', 'pro') to switch to
   * @returns {Promise<{subscription: object, tier: string, clientSecret?: string}>}
   */
  modifySubscription: (planAliasOrPriceKey) =>
    request('/subscriptions/modify-subscription', { method: 'POST', body: { priceKey: planAliasOrPriceKey } }),

  /**
   * List subscriptions (for accounts that can have multiple; if single, may just mirror getSubscription).
   * Gracefully falls back to single subscription if array not provided.
   */
  listSubscriptions: async () => {
    const resp = await request('/subscriptions');
    if (Array.isArray(resp)) return resp;
    return resp ? [resp] : [];
  }
};

// Optional: consolidated export name similar to older patterns
export default PaymentsServiceApi;